import { Link } from "react-router-dom";
import Reveal from "../components/Reveal";

type Fit = "cover" | "contain";
const SIBLINGS: Array<{
  name: string;
  tagline: string;
  description: string;
  url: string;
  wordmark: string;
  fit: Fit;
  accent: "sky" | "peach" | "mint";
  gradient: string;
  orb: string;
}> = [
  {
    name: "Chaosology",
    tagline: "Future-Driven Technology, Today's Solution.",
    description:
      "Full-service technology partner: custom application development, IT services, UI/UX design, mobile, BPO, and digital marketing.",
    url: "https://chaosology.com",
    wordmark: "/logos/chaosology.gif",
    fit: "contain",
    accent: "sky",
    gradient: "from-sky-100 via-white to-lav-50",
    orb: "#b0c7ff",
  },
  {
    name: "Boreology",
    tagline: "Covenant Candles & Hosts. Premium church supplies.",
    description:
      "Handmade church candles and communion hosts: altar candles, sacramental wafers, and liturgical goods — crafted with care.",
    url: "https://boreology.com",
    wordmark: "/logos/boreology.svg",
    fit: "contain",
    accent: "peach",
    gradient: "from-peach-50 via-white to-rose-50",
    orb: "#ffc6a3",
  },
  {
    name: "Lectureology",
    tagline: "UK Diplomas, K–12 Tuition & Test Prep, Globally.",
    description:
      "Qualifi-approved UK diplomas, one-to-one K–12 tuition, SAT & MCAT prep, and creative tracks — one online academy for the whole family.",
    url: "https://lectureology.com",
    wordmark: "/logos/lectureology-final.svg",
    fit: "contain",
    accent: "mint",
    gradient: "from-mint-50 via-white to-sky-50",
    orb: "#aee8c7",
  },
];

export default function Support() {
  return (
    <>
      <section className="container-prose pt-16 sm:pt-24 pb-10 relative">
        <div
          aria-hidden
          className="orb -top-16 right-0 h-72 w-72 animate-float"
          style={{ background: "radial-gradient(closest-side, #93ddd9, transparent)" }}
        />
        <Reveal>
          <span className="pill pill-teal">How we're funded</span>
          <h1 className="mt-5 font-display text-5xl sm:text-6xl leading-[1.02] text-balance">
            We don't fundraise — <em className="not-italic grad-text">our sibling companies do the work for us.</em>
          </h1>
          <p className="mt-6 text-lg text-ink-500 leading-relaxed">
            Sanctology Welfare Foundation is a Section 8 not-for-profit
            company. It is the CSR arm of <strong>Logy Holdings</strong>, and
            is sustained by a simple structure: the profits of our sibling
            operating companies fund the care work here. No public
            fundraising. No appeals. No donation widgets.
          </p>
        </Reveal>
      </section>

      <section className="container-prose py-10">
        <Reveal>
          <div className="glass-strong rounded-3xl p-8 sm:p-10 relative overflow-hidden">
            <div
              aria-hidden
              className="absolute -top-16 -left-16 h-56 w-56 rounded-full blur-3xl opacity-50"
              style={{ background: "radial-gradient(closest-side, #ffb6cb, transparent)" }}
            />
            <h2 className="font-display text-3xl">Why this matters</h2>
            <ul className="mt-5 space-y-4 text-ink-700 leading-relaxed">
              <li><strong className="text-ink-900">No one is asked.</strong> The elders and children we serve are never charged. The public is never asked to donate.</li>
              <li><strong className="text-ink-900">Our attention is on care.</strong> We don't run campaigns, burn budget on grant writing, or chase donors.</li>
              <li><strong className="text-ink-900">Aligned incentives.</strong> When our sibling companies do well, our home does well — their growth is directly the growth of our sanctuary.</li>
            </ul>
          </div>
        </Reveal>
      </section>

      <section className="container-xl py-14">
        <Reveal>
          <div className="text-center max-w-2xl mx-auto mb-10">
            <h2 className="font-display text-4xl sm:text-5xl">The Logy family</h2>
            <p className="mt-4 text-ink-500 leading-relaxed">
              The operating companies whose profits Sanctology depends on.
              Click through to see their work.
            </p>
          </div>
        </Reveal>
        <div className="grid gap-5 md:grid-cols-3">
          {SIBLINGS.map((s, i) => (
            <Reveal key={s.name} delay={i * 100}>
              <a
                href={s.url}
                target="_blank"
                rel="noreferrer"
                className={`group block relative rounded-3xl overflow-hidden border border-white/90 glass-lift h-full bg-gradient-to-br ${s.gradient}`}
              >
                <div
                  aria-hidden
                  className="absolute -top-10 -right-10 h-40 w-40 rounded-full blur-3xl opacity-50 group-hover:opacity-80 transition"
                  style={{ background: `radial-gradient(closest-side, ${s.orb}, transparent)` }}
                />
                <div className="aspect-[16/9] bg-white/80 border-b border-white flex items-center justify-center overflow-hidden relative">
                  <img
                    src={s.wordmark}
                    alt={`${s.name} logo`}
                    className={
                      s.fit === "cover"
                        ? "w-full h-full object-cover"
                        : "max-w-[80%] max-h-[70%] object-contain"
                    }
                    loading="lazy"
                  />
                </div>
                <div className="relative p-7">
                  <div className={`pill pill-${s.accent}`}>{s.tagline}</div>
                  <div className="font-display text-3xl mt-3">{s.name}</div>
                  <p className="text-ink-500 mt-3 text-sm leading-relaxed">{s.description}</p>
                  <div className="mt-5 inline-flex items-center gap-1 font-medium text-royal group-hover:gap-2 transition-all">
                    Visit {s.name.toLowerCase()}.com
                    <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <path d="M7 17L17 7M17 7H9M17 7V15" />
                    </svg>
                  </div>
                </div>
              </a>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="container-prose py-14">
        <Reveal>
          <div className="surface">
            <h2 className="font-display text-3xl">Want to support us anyway?</h2>
            <p className="mt-3 text-ink-700 leading-relaxed">
              The best way you can support our work is by volunteering your
              time, referring people who need sanctuary through the waitlists,
              or — if you want to support our sibling companies — hiring
              Chaosology, Boreology, or Lectureology for the work they do.
              Their success is our runway.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/volunteer" className="btn-primary">Volunteer with us →</Link>
              <Link to="/contact" className="btn-ghost">Talk to us</Link>
            </div>
          </div>
        </Reveal>
      </section>
    </>
  );
}
